import React from 'react';
import { CornerDownRightIcon } from 'lucide-react';
import { loanAdjustedCalc } from '../../data/loanCommission';
import { DIVISOR_CUTOFF_QUESTION, DIVISOR_CUTOFF_RULE } from '../../data/compPlan';
import { negativeCompLoans } from '../../data/periodClose';
import { formatPct, formatUsd } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { NewBadge } from '../sf/NewBadge';
import { OpenQuestionBadge } from '../sf/OpenQuestionBadge';
interface Term {
  label: string;
  value: string;
  emphasis?: boolean;
}
function TermBox({
  term
}: {
  term: Term;
}) {
  return <div className={`rounded border px-3 py-1.5 text-center ${term.emphasis ? 'border-sf-green bg-sf-green-light' : 'border-sf-border bg-white'}`} data-id="element-252" data-mp="6t8s058xg9sn">
      <p className="whitespace-nowrap text-[11px] text-sf-weak" data-id="element-253" data-mp="2rx0fu6hteht">{term.label}</p>
      <p className={`whitespace-nowrap tabular-nums ${term.emphasis ? 'text-[15px] font-bold' : 'text-[13px] font-semibold'}`} data-id="element-254" data-mp="2rkshn3r6u7bp">{term.value}</p>
    </div>;
}
function Operator({
  symbol
}: {
  symbol: string;
}) {
  return <span className="px-0.5 text-lg font-semibold text-sf-weak" aria-hidden="true" data-id="element-255" data-mp="3r75lda1bhndc">
      {symbol}
    </span>;
}
export function AdjustedEquation() {
  const calc = loanAdjustedCalc;
  const negative = negativeCompLoans[0];
  const sequence: (Term | string)[] = [{
    label: 'Loan amount',
    value: formatUsd(calc.loanAmount)
  }, '×', {
    label: 'Total Comp Corr',
    value: formatPct(calc.totalCompPct)
  }, '=', {
    label: 'Comp revenue',
    value: formatUsd(calc.revenue)
  }, '−', {
    label: 'Fee short (halved)',
    value: formatUsd(calc.feeShort)
  }, '=', {
    label: 'Net revenue',
    value: formatUsd(calc.revenueAfterShort)
  }, '÷', {
    label: `Divisor (${calc.divisorRuleKey})`,
    value: formatPct(calc.divisorPct)
  }, '=', {
    label: 'Adjusted Loan Amount',
    value: formatUsd(calc.adjustedAmount),
    emphasis: true
  }];
  const definitions = [{
    term: 'Total Comp Corr %',
    text: `the revenue LendingForce earns on the loan, as a % of the loan amount (here ${formatPct(calc.totalCompPct)} = ${formatUsd(calc.revenue)}).`
  }, {
    term: 'Fee short',
    text: 'when the borrower’s fees don’t cover the target, half of the shortfall is subtracted. Correspondent non-HELOC loans only.'
  }, {
    term: 'Divisor',
    text: 'the standard revenue % for the loan. Correspondent: 3.25% if the rate locked before the branch cutoff, 2.75% on or after it (Miami 2026-09-01, others 2026-09-22). Non-correspondent HELOC / HELOAN 4.00%; non-HELOC broker 3.25%. Below it, the loan is scaled by ABS(comp %) ÷ divisor; at or above it, the raw loan amount is used.'
  }];
  const order = ['Both Adjusted Total Comp Corr % and Total Comp Broker % are 0 → raw Loan Amount', 'Negative comp (Correspondent comp % < 0, or broker comp % < 0) → $0.00', 'Origination Fee > 0 → Origination Fee ÷ 3.25% (V1-DIV-ORIG)', 'Comp % below the divisor → Loan × ABS(comp %) ÷ divisor; at or above → raw Loan Amount'];
  return <div className="space-y-3" data-id="element-256" data-mp="257xw5j92klyw">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] lg:grid-cols-3" data-id="element-257" data-mp="258zi4irmisve">
        {definitions.map(item => <div key={item.term} data-id="element-258" data-mp="2a3vdnsw2jyd9">
            <dt className="inline font-semibold" data-id="element-259" data-mp="2e7kv7yebtfos">{item.term}</dt>
            <dd className="inline text-sf-text" data-id="element-260" data-mp="2e6046ir80evo"> = {item.text}</dd>
          </div>)}
      </dl>
      <ol className="list-decimal space-y-0.5 pl-5 text-xs text-sf-text" data-id="element-261" data-mp="2nxmm0xy2dc7e">
        {order.map(item => <li key={item} data-id="element-262" data-mp="2swh23s4ncmnt">{item}</li>)}
      </ol>
      <p className="flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-263" data-mp="2sv61g16hzv0x">
        {DIVISOR_CUTOFF_RULE}. <OpenQuestionBadge data-id="element-264" data-mp="2xwljdkddplf3" /> <span data-id="element-265" data-mp="2xtzextlsbm13">{DIVISOR_CUTOFF_QUESTION}</span>
      </p>
      <p className="text-xs text-sf-weak" data-id="element-266" data-mp="31w44oh943yig">
        Comp type: <span className="text-sf-text" data-id="element-267" data-mp="13hss1xjj3gsy">{calc.compType}</span> · Product: <span className="text-sf-text" data-id="element-268" data-mp="13jlxxambhwzw">{calc.product}</span> · Rate Lock Date:{' '}
        <span className="text-sf-text" data-id="element-269" data-mp="17k5wmimjh8o5">{calc.rateLockDate}</span> ({calc.divisorReason})
      </p>
      <div className="flex flex-wrap items-center gap-1.5" role="img" aria-label={`${formatUsd(calc.revenue)} minus ${formatUsd(calc.feeShort)} equals ${formatUsd(calc.revenueAfterShort)}, divided by ${formatPct(calc.divisorPct)} equals ${formatUsd(calc.adjustedAmount)}`} data-id="element-270" data-mp="c7wlrlsa95db">
        {sequence.map((item, index) => typeof item === 'string' ? <Operator key={index} symbol={item} data-id="element-271" data-mp="3pnuirqnivbkl" /> : <TermBox key={index} term={item} data-id="element-272" data-mp="3ponjoazsv6ut" />)}
      </div>
      <div className="space-y-1 text-xs text-sf-weak" data-id="element-273" data-mp="3kpauvkpdbppl">
        <p className="tabular-nums" data-id="element-274" data-mp="3kqud3gul4au0">
          Fee short = ({formatUsd(calc.threshold)} target − {formatUsd(calc.processingCollected)} processing collected − {formatUsd(calc.underwritingCollected)} underwriting) ÷ 2 ={' '}
          {formatUsd(calc.feeShort)} (= 0.0612% of the loan), so Adjusted Total Comp Corr = {calc.adjustedCompPct.toFixed(4)}%. VA IRRRL target is $555.00.
        </p>
        <p className="tabular-nums" data-id="element-275" data-mp="31y07vemrjx2z">
          Adjusted comp = {formatUsd(calc.adjustedAmount)} × 1.25% = <strong className="text-sf-text" data-id="element-276" data-mp="1hexwml6vqrfr">$4,821.15</strong> (base only; bonus 0) vs stored $5,156.25.
        </p>
        <p className="tabular-nums" data-id="element-277" data-mp="1m9aggwc9q4s1">If comp is 3.25% or higher the Adjusted Loan Amount is simply the raw loan amount (e.g. 3.40% → $412,500.00).</p>
      </div>
      <div className="flex items-start gap-2 rounded border border-dashed border-sf-amber bg-sf-amber-light px-3 py-2" data-id="element-278" data-mp="1cift1onacz50">
        <CornerDownRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-sf-amber-text" aria-hidden="true" data-id="element-279" data-mp="207i9it66olxx" />
        <div className="text-[13px]" data-id="element-280" data-mp="255jtdgbfopag">
          <p className="flex flex-wrap items-center gap-2 font-semibold text-sf-amber-text" data-id="element-281" data-mp="1r6ak70lnavfz">
            If net revenue is below $0 <Badge tone="warning" data-id="element-282" data-mp="1w4lx5k78lm2f">Negative comp</Badge>
          </p>
          <p className="text-xs tabular-nums text-sf-text" data-id="element-283" data-mp="fr5670g2dah2">
            Example {negative.loanNumber}: {formatUsd(negative.revenue)} − {formatUsd(negative.feeShort)} = {formatUsd(negative.revenueAfterShort)} → revenue after fee
            short is negative → comp % &lt; 0 → Adjusted Loan Amount <strong data-id="element-284" data-mp="2mhi77kh2p1lc">{formatUsd(negative.adjustedAmount)}</strong> (main)
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-285" data-mp="2hh2xd2w02c84">
            <NewBadge label="NEW / requested" data-id="element-286" data-mp="2co1eykwl3ujs" />
            Main already shows $0.00 adjusted for negative comp. The flat $500 LO / $250 TL amount is still NEW / requested and not applied.
          </p>
        </div>
      </div>
    </div>;
}